import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import {
  InformationCircleIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  LockClosedIcon,
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";
import { requireAuth } from "../auth"; // Adjust path as needed

// ────────────────────────────────────────────────────────────────────────────
// 1) FRAIS OFFICIELS, FOURCHETTES DE COÛTS, PALETTES DE COULEURS (Pension Specific)
// ────────────────────────────────────────────────────────────────────────────

// Frais officiels de cour
const OFFICIAL_FEES = {
  DEPOT_MODIFICATION: 355, // Frais de dépôt d'une requête en modification de pension
  DEPOT_REPONSE: 177, // Frais pour une réponse/contestation
  FORMULAIRE_H: 128, // Formulaire H pour procédure simplifiée (SAH - now defunct, keeping for reference/potential future use)
  DEMANDE_CONJOINTE: 128, // Si démarche conjointe/commune
};

// Fourchettes de coûts
const COST_RANGES = {
  AVOCAT_AMIABLE: { min: 800, max: 2500 }, // Par partie, avocats séparés, amiable
  AVOCAT_AMIABLE_PARTAGE: { min: 500, max: 1200 }, // Si partagé (moins cher)
  AVOCAT_CONTESTE_SIMPLE: { min: 2500, max: 6000 }, // Par partie, contesté simple
  AVOCAT_CONTESTE_COMPLEX: { min: 5000, max: 15000 }, // Par partie, contesté complexe
  HUISSIER: { min: 80, max: 200 }, // Par service
  TRADUCTION_PAGE: { min: 50, max: 100 }, // Par page
  FRAIS_ADMIN: { min: 50, max: 150 }, // Frais administratifs divers
};

// Coûts des expertises
const EXPERTISE_COSTS = {
  FINANCIERE: { min: 1500, max: 4000 }, // Expertise financière complète
  COMPTABLE: { min: 1000, max: 3000 }, // Expertise comptable
  REVENU_CACHE: { min: 2000, max: 6000 }, // Analyse des revenus cachés
};

// Règles spéciales
const SPECIAL_RULES = {
  URGENCE_SURCHARGE: 0.3, // +30% si urgence
  COMPLEXITE_SURCHARGE: 0.5, // +50% si complexe
};

// Palettes de couleurs (from GardeCalculator for consistency)
const COLORS = {
  primary: {
    light: "#E3F2FD",
    main: "#2196F3",
    dark: "#1565C0",
  },
  secondary: {
    light: "#F3E5F5",
    main: "#9C27B0",
    dark: "#6A1B9A",
  },
  success: {
    light: "#E8F5E9",
    main: "#4CAF50",
    dark: "#2E7D32",
  },
  error: {
    light: "#FFEBEE",
    main: "#F44336",
    dark: "#C62828",
  },
  warning: {
    light: "#FFF8E1",
    main: "#FFC107",
    dark: "#F57F17",
  },
  info: {
    light: "#E1F5FE",
    main: "#03A9F4",
    dark: "#0277BD",
  },
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
};


// ────────────────────────────────────────────────────────────────────────────
// 2) SECTIONS DU CALCULATEUR (Replaces Steps, Pension Specific)
// ────────────────────────────────────────────────────────────────────────────

const sections = [
  {
    id: "situation",
    title: "Type de procédure",
    icon: "📝",
    description:
      "Indiquez des informations de base sur votre demande de modification de pension alimentaire.",
    questions: [
      {
        name: "relationStatus",
        type: "radio",
        label: "Quel est votre statut relationnel actuel ?",
        options: [
          { value: "conjointsDeFait", label: "Ex-conjoints de fait" },
          { value: "divorces", label: "Divorcés ou en instance de divorce" },
          { value: "jamaisVecuEnsemble", label: "Parents n'ayant jamais vécu ensemble" },
        ],
        tooltip: "Votre statut détermine certaines procédures et droits spécifiques.",
      },
      {
        name: "isContested",
        type: "radio",
        label: "La modification de pension est-elle contestée ?",
        options: [
          { value: "no", label: "Non (entente amiable possible)" },
          { value: "yes", label: "Oui (contestation, désaccord)" },
        ],
        helpText: "Un dossier contesté implique généralement plus de frais d'avocat et d'expertises.",
      },
      {
        name: "hasCurrentJudgment",
        type: "radio",
        label: "Disposez-vous déjà d'un jugement ou d'une entente sur la pension actuelle ?",
        options: [
          { value: "yes", label: "Oui (requis pour une modification)" },
          { value: "no", label: "Non (un jugement initial sera nécessaire)" },
        ],
        tooltip: "Pour modifier une pension, vous devez avoir un jugement ou une entente existante.",
      },
      {
        name: "filingRole",
        type: "radio",
        label: "Votre rôle dans la procédure :",
        options: [
          { value: "demandeur", label: "Je demande la modification (demandeur)" },
          { value: "defendeur", label: "Je réponds à une demande de modification (défendeur)" },
          {
            value: "conjointe",
            label: "Demande conjointe/entente commune",
            showIf: (ans) => ans.isContested === "no",
          },
        ],
        tooltip:
          "Le demandeur paie des frais de dépôt différents du défendeur.",
      },
    ],
  },
  {
    id: "courtFees",
    title: "Frais de dépôt & Procédure",
    icon: "⚖️",
    description:
      "Estimez les frais judiciaires que vous devrez payer pour le dépôt de documents et les particularités de la procédure.",
    questions: [
      {
        name: "filingType",
        type: "radio",
        label: "Frais de dépôt applicables :",
        options: [
          {
            value: "conjointe",
            label: `Demande conjointe (${OFFICIAL_FEES.DEMANDE_CONJOINTE} $)`,
            showIf: (ans) => ans.isContested === "no" && ans.filingRole === "conjointe",
          },
          {
            value: "modification",
            label: `Dépôt d'une requête en modification (${OFFICIAL_FEES.DEPOT_MODIFICATION} $)`,
            showIf: (ans) => ans.isContested === "yes" && ans.filingRole === "demandeur",
          },
          {
            value: "modificationAmiable",
            label: `Dépôt d'une requête en modification (amiable) (${OFFICIAL_FEES.DEPOT_MODIFICATION} $)`,
            showIf: (ans) => ans.isContested === "no" && ans.filingRole !== "conjointe",
          },
          { value: "reponse", label: `Dépôt d'une réponse (${OFFICIAL_FEES.DEPOT_REPONSE} $)`, showIf: (ans) => ans.isContested === "yes" && ans.filingRole === "defendeur" },
          { value: "none", label: "Je ne paierai pas les frais de dépôt (0 $)" },
        ],
        tooltip:
          "Les frais judiciaires varient selon le type de document déposé et la procédure choisie.",
      },
      {
        name: "needUrgent",
        type: "radio",
        label: "Avez-vous besoin d'une procédure urgente/accélérée ?",
        options: [
          { value: "yes", label: "Oui (procédure accélérée, surcoûts possibles)" },
          { value: "no", label: "Non (procédure standard)" },
        ],
        helpText: "Une procédure urgente peut engendrer des frais supplémentaires mais accélérer certaines décisions.",
      },
      {
        name: "district",
        type: "select",
        label: "Dans quel district judiciaire déposerez-vous ?",
        options: [
          { value: "montreal", label: "Montréal (plus lent, délais possibles)" },
          { value: "quebec", label: "Québec" },
          { value: "laval", label: "Laval" },
          { value: "gatineau", label: "Gatineau" },
          { value: "longueuil", label: "Longueuil" },
          { value: "sherbrooke", label: "Sherbrooke" },
          { value: "troisRivieres", label: "Trois-Rivières" },
          { value: "autre", label: "Autre district" },
        ],
        tooltip:
          "Certains districts judiciaires sont plus engorgés que d'autres, comme Montréal.",
      },
    ],
  },
  {
    id: "lawyer",
    title: "Honoraires d'avocat",
    icon: "👨‍⚖️",
    description: "Estimez les honoraires d'avocat en fonction de votre situation.",
    questions: [
      {
        name: "wantLawyer",
        type: "radio",
        label: "Souhaitez-vous engager un avocat ?",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non (0 $)" },
        ],
        tooltip:
          "Un avocat peut considérablement faciliter le processus, mais représente un coût important.",
      },
      {
        name: "lawyerShared",
        type: "radio",
        label: "Partagez-vous l'avocat avec l'autre partie ?",
        options: [
          { value: "yes", label: "Oui (frais partagés)" },
          { value: "no", label: "Non (avocats séparés)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true" && ans.isContested === "no",
        helpText: "Partager un avocat peut réduire les coûts dans un dossier à l'amiable, mais assurez-vous que l'avocat peut représenter les deux parties impartialement (souvent via un mandat de rédaction).",
      },
      {
        name: "complexityLevel",
        type: "radio",
        label: "Niveau de complexité de votre dossier :",
        options: [
          { value: "simple", label: "Simple (modification mineure, revenus simples, pas d'autres enjeux)" },
          { value: "moderate", label: "Modéré (quelques points contentieux, calculs plus poussés)" },
          { value: "complex", label: "Complexe (revenus complexes, entreprises, multiples enjeux, désaccord important)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
        tooltip: "Plus votre dossier est complexe, plus les honoraires d'avocat seront élevés.",
      },
      {
        name: "lawyerFeeType",
        type: "radio",
        label: "Type de tarification",
        options: [
          { value: "range", label: "Fourchette estimée (basée sur la complexité)" },
          { value: "custom", label: "Montant personnalisé (si vous avez un devis)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
      },
      {
        name: "customFee",
        type: "number",
        label: "Montant personnalisé ($)",
        showIf: (ans) =>
          ans.wantLawyer === "true" && ans.lawyerFeeType === "custom",
      },
    ],
  },
  {
    id: "services",
    title: "Signification et traduction",
    icon: "📨🌐",
    description:
      "Ajoutez les frais d'huissier pour signifier des documents et les traductions nécessaires.",
    questions: [
      {
        name: "needHuissier",
        type: "radio",
        label: "Avez-vous besoin d'un huissier pour signifier des documents ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" },
        ],
        tooltip:
          "Un huissier est souvent nécessaire pour signifier officiellement certains documents légaux à l'autre partie, surtout en cas de contestation.",
      },
      {
        name: "huissierServices",
        type: "slider",
        label: "Nombre de significations requises",
        min: 1,
        max: 5,
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip:
          "Chaque document à signifier compte comme un service distinct (ex: requête, réponse).",
      },
      {
        name: "significationLocation",
        type: "radio",
        label: "Où se trouve l'autre partie pour la signification ?",
        options: [
          { value: "quebec", label: "Au Québec (standard)" },
          { value: "canada", label: "Ailleurs au Canada (plus complexe)" },
          { value: "international", label: "À l'étranger (délais et coûts plus élevés)" },
        ],
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip: "La localisation de l'autre partie peut influencer les frais et délais de signification.",
      },
      {
        name: "needTranslation",
        type: "radio",
        label: "Avez-vous des documents financiers ou légaux à traduire ?",
        options: [
          { value: "no", label: "Non (0 $)" },
          { value: "yes", label: "Oui" },
        ],
        tooltip:
          "La traduction officielle peut être nécessaire si des documents importants (preuves de revenus, jugements étrangers) sont dans une autre langue.",
      },
      {
        name: "translationPages",
        type: "slider",
        label: "Nombre de pages à traduire",
        min: 1,
        max: 50,
        showIf: (ans) => ans.needTranslation === "yes",
      },
    ],
  },
  {
    id: "expertise",
    title: "Expertises financières",
    icon: "💹",
    description:
      "Dans certains cas, des expertises financières peuvent être nécessaires pour déterminer la capacité de paiement ou les revenus réels.",
    questions: [
      {
        name: "needExpertise",
        type: "radio",
        label: "Votre situation nécessite-t-elle une expertise financière ?",
        options: [
          { value: "no", label: "Non, ce n'est pas nécessaire" },
          { value: "yes", label: "Oui, j'en aurai besoin" },
          { value: "unsure", label: "Je ne suis pas sûr(e)" },
        ],
        tooltip:
          "Une expertise peut être nécessaire pour évaluer les revenus complexes (travailleur autonome, entreprise), les avantages sociaux, ou si des revenus cachés sont suspectés.",
      },
      {
        name: "expertiseType",
        type: "checkbox",
        label: "Quels types d'expertise financière pourraient être nécessaires ?",
        options: [
          {
            value: "FINANCIERE",
            label: `Expertise financière complète (${EXPERTISE_COSTS.FINANCIERE.min}$ - ${EXPERTISE_COSTS.FINANCIERE.max}$)`,
            description: "Analyse globale des revenus, actifs et passifs."
          },
          {
            value: "COMPTABLE",
            label: `Expertise comptable (${EXPERTISE_COSTS.COMPTABLE.min}$ - ${EXPERTISE_COSTS.COMPTABLE.max}$)`,
            description: "Analyse spécifique des livres comptables d'une entreprise ou d'un travailleur autonome."
          },
          {
            value: "REVENU_CACHE",
            label: `Analyse de revenus cachés ou dissimulés (${EXPERTISE_COSTS.REVENU_CACHE.min}$ - ${EXPERTISE_COSTS.REVENU_CACHE.max}$)`,
            description: "Recherche approfondie en cas de suspicion de revenus non déclarés."
          },
        ],
        showIf: (ans) => ans.needExpertise === "yes" || ans.needExpertise === "unsure",
        tooltip: "Sélectionnez tous les types d'expertise qui pourraient être nécessaires dans votre situation. Les coûts s'additionnent.",
      },
      {
        name: "adminFees",
        type: "radio",
        label: "Prévoyez-vous des frais administratifs divers (copies, envois, etc.) ?",
        options: [
          { value: "minimal", label: "Minimaux (environ 50 $)" },
          { value: "standard", label: "Standards (environ 100 $)" },
          { value: "extensive", label: "Importants (environ 150 $)" },
          { value: "none", label: "Aucun (0 $)" },
        ],
        tooltip: "Les frais administratifs incluent les photocopies, envois postaux, certifications de documents, etc.",
      },
    ],
  },
  {
    id: "summary",
    title: "Résumé et économies",
    icon: "💰",
    description:
      "Consultez votre estimation et découvrez comment économiser lors de la modification de pension alimentaire.",
    questions: [],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// 3) HOOKS ET COMPOSANTS UTILITAIRES (Adapted from GardeCalculator)
// ────────────────────────────────────────────────────────────────────────────

// Hook localStorage
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log("Error reading from localStorage:", error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log("Error writing to localStorage:", error);
    }
  };

  return [storedValue, setValue];
}

// Composant pour l'affichage des coûts détaillés
function CostBreakdownDisplay({ lineItems }) {
  const totalMin = lineItems.reduce((sum, item) => sum + item.min, 0);
  // Recalculate percentages based on the min value relative to total min cost
  const itemsWithPercentage = lineItems.map(item => ({
    ...item,
    percentage: totalMin > 0 ? Math.round((item.min / totalMin) * 100) : 0,
  }));

  return (
    <div className="w-full space-y-4">
      {itemsWithPercentage
        .filter(item => item.min > 0 || item.max > 0) // Only show items with cost
        .sort((a, b) => b.min - a.min) // Sort by min cost descending
        .map((item, index) => (
        <div key={index} className="w-full">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-slate-700">{item.label}</span>
            <span className="text-sm font-medium text-slate-500">{item.percentage}%</span>
          </div>
          <progress
            className="progress progress-primary w-full h-2" // Adjusted height
            value={item.percentage}
            max="100"
            style={{backgroundColor: COLORS.primary.light, color: COLORS.primary.main}}
          ></progress>
          <div className="text-xs text-slate-500 text-right mt-1">
            {item.min.toLocaleString()}$ - {item.max.toLocaleString()}$
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 4) COMPOSANT PRINCIPAL (PensionCalculator Refactored)
// ────────────────────────────────────────────────────────────────────────────

function PensionCalculator() {
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const [completedSections, setCompletedSections] = useLocalStorage("pensionCostCompletedSections", {});
  const [answers, setAnswers] = useLocalStorage("pensionCostAnswers", {});
  const [lineItems, setLineItems] = useState([]);
  const [totalCost, setTotalCost] = useState({ min: 0, max: 0 });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLegalAidInfo, setShowLegalAidInfo] = useState(true);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);

  // Calculate progress percentage
  const progressPercent = Math.round(
    (sections.findIndex(s => completedSections[s.id]) / (sections.length - 1)) * 100
  );

  useEffect(() => {
    calculateCosts();
    // Mark section complete automatically if all required questions are answered
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
       // Only mark as complete if not already marked
      if (!completedSections[activeSectionId]) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, activeSectionId]); // Removed completedSections dependency to avoid loops


  const handleChange = (name, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev, [name]: value };
      // If a question is changed, potentially mark subsequent sections incomplete? Or handle dynamically.
      // For now, just update the answer. Re-validation happens on navigation.
      try {
        localStorage.setItem("pensionCostAnswers", JSON.stringify(newAnswers));
      } catch (e) {
        console.log("Error saving to localStorage:", e);
      }
      return newAnswers;
    });
    // Recalculate costs immediately on change
    calculateCosts();
     // Check if current section is now complete after change
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
       if (!completedSections[activeSectionId]) {
          setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
       }
    } else {
      // If a change makes the section incomplete, mark it as such
      if (completedSections[activeSectionId]) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: false }));
      }
    }
  };


  // Navigate to next section
  const handleNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
     // Ensure current section is complete before moving
     if (isSectionComplete(activeSectionId)) {
        // Explicitly mark as complete before navigating
        if (!completedSections[activeSectionId]) {
          setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
        }
        if (currentIndex < sections.length - 1) {
          setActiveSectionId(sections[currentIndex + 1].id);
          window.scrollTo(0, 0); // Scroll to top on section change
        }
      } else {
        // Optional: Provide feedback that the section needs completion
        console.log("Please complete the current section before proceeding.");
        // Maybe shake the incomplete fields?
      }
  };

  // Navigate to previous section
  const handlePrevSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex > 0) {
      setActiveSectionId(sections[currentIndex - 1].id);
      window.scrollTo(0, 0); // Scroll to top on section change
    }
  };

  // Go to a specific section
  const goToSection = (sectionId) => {
     const targetIndex = sections.findIndex(s => s.id === sectionId);
     const currentIndex = sections.findIndex(s => s.id === activeSectionId);

     // Allow navigation only to already visited or next available section
     let canNavigate = false;
     if (targetIndex <= currentIndex) {
       canNavigate = true; // Can always go back
     } else {
       // Check if all *previous* sections are complete
       let previousSectionsComplete = true;
       for (let i = 0; i < targetIndex; i++) {
          // Check completion status based on `completedSections` state
          if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
             previousSectionsComplete = false;
             break;
           }
       }
       if (previousSectionsComplete) {
         canNavigate = true;
       }
     }

     if (canNavigate) {
       setActiveSectionId(sectionId);
       window.scrollTo(0, 0);
     } else {
       // Optionally show a message indicating sections must be completed first
       console.log("Please complete previous sections first.");
       // Could add a visual indicator like a toast notification
     }
  };


  const handleReset = () => {
    if (showResetConfirm) {
      setAnswers({});
      setCompletedSections({}); // Reset completion status
      setShowResetConfirm(false);
      setActiveSectionId(sections[0].id); // Go back to the first section
      window.scrollTo(0, 0);
    } else {
      setShowResetConfirm(true);
      // Hide confirm button after 5 seconds if not clicked
      setTimeout(() => setShowResetConfirm(false), 5000);
    }
  };

  // Check if all required questions in a section are answered
  const isSectionComplete = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section || section.questions.length === 0) return true; // Summary section is always complete

    return section.questions.every((q) => {
      // Skip questions that aren't currently shown based on conditional logic
      if (q.showIf && !q.showIf(answers)) return true;

      const answer = answers[q.name];

      // Specific check for checkboxes: Needs to be an array (even empty is considered answered if needed)
      if (q.type === 'checkbox') {
         // For checkboxes, 'answered' might mean the question was presented, even if none are checked.
         // If a selection is *required*, add a `required: true` flag to the question and check `answer?.length > 0`.
         // For now, consider it complete if the key exists (meaning it was rendered).
         return answer !== undefined;
      }
      // Check for sliders - needs a value (even the default min)
      if (q.type === 'slider') {
          return answer !== undefined && answer !== null && answer !== "";
      }
       // Default check for other types (radio, select, number)
      return answer !== undefined && answer !== null && answer !== "";
    });
  };


  // ────────────────────────────────────────────────────────────────────────────
  // 5) CALCUL DES COÛTS (Adapted logic from original PensionCalculator)
  // ────────────────────────────────────────────────────────────────────────────

  const calculateCosts = () => {
    let minSum = 0;
    let maxSum = 0;
    const newLineItems = [];

    // 1. Frais de cour
    if (answers.filingType && answers.filingType !== "none") {
      let fee = 0;
      let label = "Frais de dépôt au tribunal";
      switch (answers.filingType) {
        case "conjointe":
          fee = OFFICIAL_FEES.DEMANDE_CONJOINTE;
          label = "Frais de dépôt (demande conjointe)";
          break;
        case "modification":
          fee = OFFICIAL_FEES.DEPOT_MODIFICATION;
           label = "Frais de dépôt (requête modification)";
          break;
        case "modificationAmiable":
          fee = OFFICIAL_FEES.DEPOT_MODIFICATION;
           label = "Frais de dépôt (modif. amiable)";
          break;
        case "reponse":
          fee = OFFICIAL_FEES.DEPOT_REPONSE;
           label = "Frais de dépôt (réponse)";
          break;
        default:
          fee = 0;
      }
      if (fee > 0) {
          newLineItems.push({
            label: label,
            min: fee,
            max: fee,
            icon: "⚖️",
            percentage: 0, // Recalculated later
          });
          minSum += fee;
          maxSum += fee;
      }
    }

    // 2. Honoraires d'avocat
    if (answers.wantLawyer === "true") {
      let lawyerCostMin, lawyerCostMax;
      if (answers.lawyerFeeType === "custom" && answers.customFee && !isNaN(Number(answers.customFee))) {
        const custom = Number(answers.customFee);
        lawyerCostMin = custom;
        lawyerCostMax = custom;
      } else {
        const isContested = answers.isContested === "yes";
        let range;

        if (!isContested) {
          // Cas amiable
          range = answers.lawyerShared === "yes"
            ? COST_RANGES.AVOCAT_AMIABLE_PARTAGE
            : COST_RANGES.AVOCAT_AMIABLE;
        } else {
          // Cas contesté - simple ou complexe
          range = answers.complexityLevel === "complex"
            ? COST_RANGES.AVOCAT_CONTESTE_COMPLEX
            : answers.complexityLevel === "moderate"
            ? COST_RANGES.AVOCAT_CONTESTE_SIMPLE // Use simple range for moderate contested for now
            : COST_RANGES.AVOCAT_CONTESTE_SIMPLE; // Default to simple contested
        }

        lawyerCostMin = range.min;
        lawyerCostMax = range.max;

        // Appliquer majoration pour complexité (only if NOT custom fee)
        if (answers.complexityLevel === "complex") {
          lawyerCostMin *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE);
          lawyerCostMax *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE);
        } else if (answers.complexityLevel === "moderate") {
           // Apply half surcharge for moderate
          lawyerCostMin *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2);
          lawyerCostMax *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2);
        }
      }

      // Appliquer majoration pour urgence (if applicable, regardless of fee type)
      if (answers.needUrgent === "yes") {
        lawyerCostMin *= (1 + SPECIAL_RULES.URGENCE_SURCHARGE);
        lawyerCostMax *= (1 + SPECIAL_RULES.URGENCE_SURCHARGE);
      }

       if (lawyerCostMin > 0 || lawyerCostMax > 0) {
          newLineItems.push({
            label: "Honoraires d'avocat",
            min: Math.round(lawyerCostMin),
            max: Math.round(lawyerCostMax),
            icon: "👨‍⚖️",
            percentage: 0,
          });
          minSum += Math.round(lawyerCostMin);
          maxSum += Math.round(lawyerCostMax);
        }
    }

    // 3. Frais d'huissier
    if (answers.needHuissier === "yes" && answers.huissierServices && Number(answers.huissierServices) > 0) {
      const services = Number(answers.huissierServices);
      const huissierCost = COST_RANGES.HUISSIER;
      let minHuissier = huissierCost.min * services;
      let maxHuissier = huissierCost.max * services;

      // Adjustments for location
      if (answers.significationLocation === "canada") {
        minHuissier *= 1.5;
        maxHuissier *= 1.5;
      } else if (answers.significationLocation === "international") {
        minHuissier *= 3; // Increased multiplier for international
        maxHuissier *= 3;
      }

      newLineItems.push({
        label: `Frais d'huissier (${services} signification${services > 1 ? 's' : ''})`,
        min: Math.round(minHuissier),
        max: Math.round(maxHuissier),
        icon: "📨",
        percentage: 0,
      });
      minSum += Math.round(minHuissier);
      maxSum += Math.round(maxHuissier);
    }

    // 4. Traductions
    if (answers.needTranslation === "yes" && answers.translationPages && Number(answers.translationPages) > 0) {
      const pages = Number(answers.translationPages);
      const tradCost = COST_RANGES.TRADUCTION_PAGE;
      const minTrad = tradCost.min * pages;
      const maxTrad = tradCost.max * pages;
      newLineItems.push({
        label: `Traduction (${pages} page${pages > 1 ? 's' : ''})`,
        min: Math.round(minTrad),
        max: Math.round(maxTrad),
        icon: "🌐",
        percentage: 0,
      });
      minSum += Math.round(minTrad);
      maxSum += Math.round(maxTrad);
    }

    // 5. Expertises financières
    if ((answers.needExpertise === "yes" || answers.needExpertise === "unsure") &&
        answers.expertiseType && answers.expertiseType.length > 0) {

      answers.expertiseType.forEach((type) => {
        let expertiseCost;
        let label;

        switch (type) {
          case "FINANCIERE": // Match Checkbox value/COSTS key
            expertiseCost = EXPERTISE_COSTS.FINANCIERE;
            label = "Expertise financière";
            break;
          case "COMPTABLE":
            expertiseCost = EXPERTISE_COSTS.COMPTABLE;
            label = "Expertise comptable";
            break;
          case "REVENU_CACHE":
            expertiseCost = EXPERTISE_COSTS.REVENU_CACHE;
            label = "Analyse revenus cachés";
            break;
          default:
            return; // Skip if type is unknown
        }

        newLineItems.push({
          label,
          min: expertiseCost.min,
          max: expertiseCost.max,
          icon: "💹",
          percentage: 0,
        });
        minSum += expertiseCost.min;
        maxSum += expertiseCost.max;
      });
    }

    // 6. Frais administratifs
    if (answers.adminFees && answers.adminFees !== "none") {
      let adminMin = 0, adminMax = 0;

      switch (answers.adminFees) {
        case "minimal":
          adminMin = COST_RANGES.FRAIS_ADMIN.min;
          adminMax = COST_RANGES.FRAIS_ADMIN.min; // Use min for minimal
          break;
        case "standard":
          adminMin = COST_RANGES.FRAIS_ADMIN.min; // Mid-range
          adminMax = COST_RANGES.FRAIS_ADMIN.max;
          break;
        case "extensive":
          adminMin = COST_RANGES.FRAIS_ADMIN.max; // Use max for extensive
          adminMax = COST_RANGES.FRAIS_ADMIN.max;
          break;
        default:
          adminMin = adminMax = 0;
      }

      if (adminMin > 0 || adminMax > 0) {
          newLineItems.push({
            label: "Frais administratifs",
            min: adminMin,
            max: adminMax,
            icon: "📋",
            percentage: 0,
          });
          minSum += adminMin;
          maxSum += adminMax;
      }
    }

    setLineItems(newLineItems);
    setTotalCost({ min: Math.round(minSum), max: Math.round(maxSum) });
  };


  // Simule le partage (kept from GardeCalculator)
  const shareSummary = () => {
    // Implement actual sharing logic here (e.g., copy link, generate text)
    navigator.clipboard.writeText(`Mon estimation de coûts pour la modification de pension: ${totalCost.min}$ - ${totalCost.max}$. Détails: ${JSON.stringify(answers)}`)
      .then(() => {
        alert("Résumé copié dans le presse-papiers !");
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        alert("Erreur lors de la copie du résumé.");
      });
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 6) RENDU DES QUESTIONS ET DU RÉSUMÉ (Adapted from GardeCalculator)
  // ────────────────────────────────────────────────────────────────────────────

  // Specific render function for slider inputs
  const renderSlider = (question) => {
    const value = parseInt(answers[question.name]) || question.min;
    return (
      <div className="w-full">
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={question.min}
            max={question.max}
            value={value}
            onChange={(e) => handleChange(question.name, e.target.value)}
            className="range range-primary w-full"
          />
          <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full w-16 text-center">
            {value}
          </span>
        </div>
         <div className="flex justify-between text-xs text-slate-500 mt-1 px-1">
          <span>{question.min}</span>
          <span>{question.max}</span>
        </div>
      </div>
    );
  };

  // Renders individual questions based on type (Adapted from GardeCalculator)
  const renderQuestion = (question) => {
    // Conditional rendering check
    if (question.showIf && !question.showIf(answers)) {
      return null;
    }

    const questionKey = `${activeSectionId}-${question.name}`; // Unique key per question instance

    return (
       <div key={questionKey} className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
         <div className="flex items-start justify-between gap-2">
           <label className="font-medium text-slate-800 flex-1">
             {question.label}
           </label>
           {question.tooltip ? (
             <>
               <Tooltip id={`tooltip-${questionKey}`} place="top" className="z-50 max-w-xs" />
               <button
                 type="button"
                 className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                 data-tooltip-id={`tooltip-${questionKey}`}
                 data-tooltip-content={question.tooltip}
                 aria-label="Plus d'informations"
               >
                 <InformationCircleIcon className="w-5 h-5" />
               </button>
             </>
           ) : null}
         </div>

         {/* Question inputs based on type */}
         <div className="mt-3 space-y-2">
            {question.type === "radio" && (
              <div className="space-y-2">
                {question.options.map((opt, i) => {
                  if (opt.showIf && !opt.showIf(answers)) return null;
                  const isChecked = answers[question.name] === opt.value;
                  const optionKey = `${questionKey}-opt-${i}`;
                  return (
                    <label
                      key={optionKey}
                      className={`cursor-pointer flex items-start gap-3 p-3 rounded-md border transition-colors ${
                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={question.name}
                        value={opt.value}
                        checked={isChecked}
                        onChange={(e) => handleChange(question.name, e.target.value)}
                        className="radio radio-primary mt-1 flex-shrink-0"
                      />
                      <span className="flex-1">
                        <span className="text-slate-700 block">{opt.label}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {question.type === "checkbox" && (
               <div className="space-y-3">
                 {question.options.map((opt, i) => {
                    if (opt.showIf && !opt.showIf(answers)) return null;
                    const isChecked = answers[question.name]?.includes(opt.value) || false;
                    const optionKey = `${questionKey}-opt-${i}`;
                    return (
                       <div key={optionKey} className={`rounded-lg border p-4 transition-colors ${isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                         <label className="flex items-start gap-3 cursor-pointer">
                           <input
                             type="checkbox"
                             value={opt.value}
                             checked={isChecked}
                             onChange={(e) => {
                               const currentValues = answers[question.name] || [];
                               const newValues = e.target.checked
                                 ? [...currentValues, opt.value]
                                 : currentValues.filter(v => v !== opt.value);
                               handleChange(question.name, newValues);
                             }}
                             className="mt-1 checkbox checkbox-primary flex-shrink-0"
                           />
                           <div className="flex-1">
                             <div className="font-medium text-slate-700">{opt.label}</div>
                             {opt.description && (
                               <p className="text-slate-500 mt-1">{opt.description}</p>
                             )}
                           </div>
                         </label>
                       </div>
                    )
                 })}
               </div>
            )}

            {question.type === "select" && (
              <select
                className="select select-bordered w-full"
                value={answers[question.name] || ""}
                onChange={(e) => handleChange(question.name, e.target.value)}
              >
                <option value="" disabled>Sélectionnez...</option>
                {question.options.map((o, i) => (
                  <option key={i} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}

            {question.type === "slider" && (
              renderSlider(question) // Use the dedicated slider function
            )}

             {question.type === "number" && (
                <div className="relative mt-1 rounded-md shadow-sm w-full max-w-xs">
                    <input
                        type="number"
                        value={answers[question.name] || ""}
                        onChange={(e) => handleChange(question.name, e.target.value)}
                        className="input input-bordered w-full pr-12" // Added padding for '$' sign
                        placeholder="0"
                        min="0" // Ensure non-negative values
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                </div>
             )}
         </div>
           {/* Help Text below input */}
           {question.helpText && !question.tooltip && (
            <p className="text-xs text-slate-500 mt-2">{question.helpText}</p>
           )}
       </div>
    );
  };


  // Pension specific saving tips
  const PENSION_SAVING_TIPS = [
    {
      title: "Utilisez le SARPA si possible",
      description: "Le Service administratif de rajustement des pensions alimentaires (SARPA) permet de modifier une pension sans frais judiciaires dans certains cas (revenus changés, etc.). Vérifiez votre admissibilité.",
      potential_savings: "Frais de cour et avocat (si applicable)",
      difficulty: "Facile",
      relevance: () => true // Always relevant to check
    },
    {
      title: "Négociez une entente à l'amiable",
      description: "Avant d'aller au tribunal, tentez de discuter et de convenir d'une modification avec l'autre parent. Vous pouvez utiliser le formulaire de fixation pour vous guider.",
      potential_savings: "Frais de cour, avocat, expertises",
      difficulty: "Modérée à Difficile",
      relevance: (ans) => ans.isContested === 'no'
    },
     {
      title: "Optez pour une demande conjointe",
      description: "Si vous vous entendez, déposer une demande conjointe réduit les frais de cour et simplifie la procédure.",
      potential_savings: `${OFFICIAL_FEES.DEPOT_MODIFICATION - OFFICIAL_FEES.DEMANDE_CONJOINTE}$ en frais de cour`,
      difficulty: "Facile (si accord)",
      relevance: (ans) => ans.isContested === 'no' && ans.filingRole !== 'conjointe'
    },
    {
      title: "Préparez vos preuves de revenus",
      description: "Ayez en main tous les documents prouvant le changement de revenus (avis de cotisation, talons de paie, états financiers si travailleur autonome) pour accélérer le processus.",
      potential_savings: "Temps (et donc argent) d'avocat",
      difficulty: "Facile",
      relevance: (ans) => ans.wantLawyer === 'true'
    },
     {
      title: "Vérifiez l'aide juridique",
      description: "Si vos revenus sont bas, l'aide juridique peut couvrir une partie ou la totalité de vos frais pour la modification de pension.",
      potential_savings: "Totalité ou partie des frais",
      difficulty: "Facile",
      relevance: () => true
    }
  ];

  // Pension specific FAQ
  const faqItems = [
    {
      question: "À quelle fréquence peut-on modifier une pension alimentaire ?",
      answer: "Une modification peut être demandée dès qu'un changement 'significatif et imprévu' survient dans la situation d'un des parents (perte d'emploi, augmentation de salaire) ou dans les besoins de l'enfant (frais particuliers). Il n'y a pas de fréquence fixe."
    },
    {
      question: "Comment se calcule le montant d'une pension alimentaire ?",
      answer: "Au Québec, elle est calculée principalement selon le 'Modèle québécois de fixation des pensions alimentaires pour enfants'. Il prend en compte les revenus des deux parents, le nombre d'enfants, le temps de garde et certains frais particuliers."
    },
    {
      question: "Peut-on modifier une pension sans avocat ?",
      answer: "Oui, c'est possible, surtout si la modification est consensuelle (demande conjointe) ou via le SARPA. Cependant, si la situation est complexe ou contestée, l'aide d'un avocat est fortement recommandée pour protéger vos droits."
    },
    {
      question: "Qu'est-ce que le SARPA ?",
      answer: "Le Service administratif de rajustement des pensions alimentaires pour enfants (SARPA) est un service gouvernemental qui permet de modifier un jugement de pension alimentaire sans retourner devant le tribunal, sous certaines conditions (ex: changement de revenu annuel)."
    },
    {
      question: "Les frais d'avocat sont-ils déductibles d'impôt ?",
      answer: "Les frais juridiques engagés pour obtenir ou modifier une pension alimentaire pour enfants ne sont généralement PAS déductibles d'impôt au Québec et au Canada. Consultez un fiscaliste pour votre situation spécifique."
    }
  ];

  // Info "Aide juridique" (adapted for Pension)
  const renderLegalAidInfo = () => (
    <div
      className={`bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 transition-all duration-300 ${
        showLegalAidInfo ? "opacity-100" : "opacity-0 h-0 overflow-hidden p-0 m-0"
      }`}
      style={{
        backgroundColor: COLORS.info.light,
        borderLeftColor: COLORS.info.main,
      }}
    >
      <div className="flex justify-between items-start">
        <h3
          className="text-blue-800 font-semibold mb-2"
          style={{ color: COLORS.info.dark }}
        >
          Aide juridique & SARPA
        </h3>
        <button
          onClick={() => setShowLegalAidInfo(false)}
          className="text-gray-500 hover:text-gray-700 -mt-1 -mr-1" // Adjust button position
          aria-label="Fermer"
        >
          <XCircleIcon className="w-5 h-5"/>
        </button>
      </div>
      <p className="text-sm text-gray-700 mb-2">
        <strong>
          Si vos revenus sont bas, vérifiez votre admissibilité à l'aide juridique.
        </strong>{" "}
        Vous pourriez bénéficier d'un avocat gratuit ou à faible coût.{" "}
        <a
          href="https://www.csj.qc.ca/commission-des-services-juridiques/aide-juridique/admissibilite/fr"
          className="text-blue-600 hover:underline ml-1 font-medium"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: COLORS.primary.main }}
        >
          Vérifier l'admissibilité (CSJ)
        </a>
      </p>
      <p className="text-sm text-gray-700">
        Le <strong style={{color: COLORS.info.dark }}>SARPA</strong> peut aussi vous aider à modifier une pension sans passer par les tribunaux dans certains cas.{" "}
         <a
          href="https://www.sarpaquebec.ca/" // Link to SARPA website
          className="text-blue-600 hover:underline ml-1 font-medium"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: COLORS.primary.main }}
        >
          En savoir plus (SARPA)
        </a>
      </p>
    </div>
  );

  // Renders a single section (Adapted from GardeCalculator)
  const renderSection = (section) => {
    const isCurrent = section.id === activeSectionId;
    const isCompleted = completedSections[section.id] || false; // Check our state
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    const isSummary = section.id === 'summary';

     // Determine if the section is accessible
    let isAccessible = false;
     if (sectionIndex === 0) {
         isAccessible = true; // First section always accessible
       } else {
         // Check if all *previous* sections are complete
         let previousSectionsComplete = true;
         for (let i = 0; i < sectionIndex; i++) {
            if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
              previousSectionsComplete = false;
              break;
            }
         }
         isAccessible = previousSectionsComplete;
      }

    // Automatically mark summary as complete if all previous sections are complete
    if (isSummary && isAccessible && !completedSections[section.id]) {
       setCompletedSections(prev => ({ ...prev, [section.id]: true }));
    }

    return (
      <div key={section.id} className={`mb-6 rounded-xl border shadow-sm transition-all duration-300 ${
        isCurrent ? 'border-blue-300 bg-blue-50 shadow-md' : isCompleted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
      } ${isAccessible ? 'opacity-100' : 'opacity-60 cursor-not-allowed'}`}>
        <button
          className={`w-full p-5 text-left flex justify-between items-center ${isAccessible ? 'cursor-pointer hover:bg-opacity-50' : 'cursor-not-allowed'} ${isCurrent ? '' : isCompleted ? 'hover:bg-green-100' : 'hover:bg-slate-50'}`}
          onClick={() => isAccessible && goToSection(section.id)}
          disabled={!isAccessible}
          aria-expanded={isCurrent}
          aria-controls={`section-content-${section.id}`}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-xl ${isCompleted && !isCurrent ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-slate-500'}`}>
              {section.icon}
            </span>
            <h2 className={`text-lg font-semibold ${isCompleted && !isCurrent ? 'text-green-800' : isCurrent ? 'text-blue-800' : 'text-slate-700'}`}>
              {section.title}
            </h2>
          </div>
          <div className="flex items-center">
            {isCompleted && !isCurrent && (
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" aria-label="Complété"/>
            )}
             <span className={`transform transition-transform duration-200 ${isCurrent ? 'rotate-180' : 'rotate-0'}`}>
               {isAccessible ? <ChevronDownIcon className="w-5 h-5 text-slate-500" /> : <LockClosedIcon className="w-5 h-5 text-slate-400" aria-label="Verrouillé"/> }
             </span>
          </div>
        </button>

        {/* Collapsible Content */}
        {isCurrent && (
          <div id={`section-content-${section.id}`} className="px-5 pb-6 pt-2 border-t border-slate-200">
             {isSummary ? (
               // Render Summary Content for Pension
               renderCostSummary() // Calls the dedicated summary function
             ) : (
               // Render Question Section Content
               <>
                 <p className="text-sm text-slate-600 mb-6">{section.description}</p>
                 <div className="space-y-6">
                  {section.questions.map((question) => renderQuestion(question))}
                  </div>
                  {/* Navigation Buttons */}
                  <div className="mt-8 flex justify-between items-center pt-4 border-t border-slate-200">
                    <div>
                      {sectionIndex > 0 && (
                        <button
                          onClick={handlePrevSection}
                          className="btn btn-outline btn-primary btn-sm gap-1"
                        >
                          <ArrowLeftCircleIcon className="w-4 h-4" />
                          Précédent
                        </button>
                      )}
                    </div>
                    <div>
                      {sectionIndex < sections.length - 1 && (
                        <button
                          onClick={handleNextSection}
                          className={`btn btn-primary btn-sm gap-1 ${!isSectionComplete(section.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          // disabled={!isSectionComplete(section.id)} // Disabling directly might be less clear
                        >
                          { isSectionComplete(section.id) ? 'Suivant' : 'Complétez la section'}
                           {!isSectionComplete(section.id) && <LockClosedIcon className="w-4 h-4 ml-1" />}
                           {isSectionComplete(section.id) && <ArrowRightCircleIcon className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
      </div>
    );
  };


  // Renders the final summary section (Adapted from GardeCalculator)
  const renderCostSummary = () => {
    // Filter tips specifically for Pension scenarios
    const relevantTips = PENSION_SAVING_TIPS.filter(tip => tip.relevance(answers));

    return (
      <div className="space-y-8">
        {/* Top Summary: Total Cost & Key Factors */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Total Cost Card */}
          <div className="card bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg">
            <h3 className="font-semibold mb-3 text-lg flex items-center opacity-90">
              <span className="mr-2">💰</span> Estimation Totale (Pension)
            </h3>
            <p className="text-4xl font-bold">
              {totalCost.min.toLocaleString()}$ - {totalCost.max.toLocaleString()}$
            </p>
            <p className="opacity-80 mt-1 text-sm">Coût total estimé de la procédure</p>
            {/* Disclaimer */}
            <p className="text-xs opacity-70 mt-3 italic">
              Estimation indicative. Les coûts réels varient.
            </p>
          </div>
           {/* Key Factors Card */}
          <div className="card bg-white border border-slate-200 p-6 rounded-xl shadow">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center">
              <span className="mr-2">📊</span> Facteurs Clés Influents
            </h3>
             <ul className="space-y-2 text-sm">
               {/* Dynamically show key factors based on answers */}
                <li className="flex justify-between items-center">
                  <span className="text-slate-600">Type:</span>
                  <span className={`font-medium px-2 py-0.5 rounded text-xs ${answers.isContested === 'yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                    {answers.isContested === 'yes' ? 'Contesté' : 'À l\'amiable'}
                  </span>
                </li>
                 <li className="flex justify-between items-center">
                   <span className="text-slate-600">Avocat:</span>
                    <span className={`font-medium px-2 py-0.5 rounded text-xs ${answers.wantLawyer === 'true' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'}`}>
                      {answers.wantLawyer === 'true' ? 'Oui' : 'Non'}
                    </span>
                 </li>
                {answers.wantLawyer === 'true' && (
                  <li className="flex justify-between items-center">
                    <span className="text-slate-600">Complexité:</span>
                     <span className="font-medium capitalize px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                       {answers.complexityLevel || 'Simple'}
                     </span>
                  </li>
                 )}
                 {answers.needExpertise === 'yes' && answers.expertiseType?.length > 0 && (
                   <li className="flex justify-between items-center">
                     <span className="text-slate-600">Expertise(s):</span>
                      <span className="font-medium px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                        {answers.expertiseType.join(', ')}
                      </span>
                   </li>
                 )}
             </ul>
             <p className="text-xs text-slate-400 mt-3 italic">Principaux éléments affectant le coût.</p>
          </div>
        </div>

        {/* Detailed Cost Breakdown Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">📊</span>
            Détail des coûts estimés
          </h2>
          {lineItems.length > 0 && (totalCost.min > 0 || totalCost.max > 0) ? (
            <CostBreakdownDisplay lineItems={lineItems} />
          ) : (
            <div className="flex flex-col items-center justify-center h-32 bg-slate-50 rounded-lg">
              <ExclamationCircleIcon className="w-12 h-12 text-slate-300 mb-2" />
              <p className="text-center text-slate-500 italic">
                {answers.wantLawyer === 'false' && !answers.filingType && !answers.needHuissier && !answers.needTranslation && !answers.needExpertise && !answers.adminFees
                  ? "Aucun coût sélectionné."
                  : "Complétez les sections pour voir le détail."}
              </p>
            </div>
          )}
        </div>

        {/* Contextual Saving Tips Card */}
        <div className="bg-green-50 rounded-xl border border-green-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
            <span className="bg-green-100 p-2 rounded-full">💡</span>
            Astuces personnalisées pour économiser (Pension)
          </h2>
          <div className="space-y-4">
            {relevantTips.length > 0 ? relevantTips.slice(0, 3).map((tip, index) => ( // Show top 3 relevant tips
              <div key={index} className="flex items-start gap-4 bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                <div className="bg-green-100 p-2 rounded-full flex-shrink-0 mt-1">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">{tip.title}</h4>
                  <p className="text-green-700 text-sm mt-1">{tip.description}</p>
                  {tip.potential_savings && <p className="text-xs text-green-600 font-medium mt-1">Économie potentielle: <span className="font-bold">{tip.potential_savings}</span></p>}
                </div>
              </div>
            )) : <p className="text-green-700 italic text-sm">Aucune astuce spécifique pour le moment basée sur vos réponses.</p>}
          </div>
        </div>

        {/* Next Steps Card */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 p-2 rounded-full">🚀</span>
            Prochaines étapes suggérées
          </h2>
          <div className="space-y-3 text-sm">
             <p className="text-blue-800">Cette estimation est un guide. Voici des actions utiles :</p>
            <ul className="list-disc list-inside space-y-2 pl-2 text-blue-700">
              {answers.isContested === 'no' && <li><a href="https://www.sarpaquebec.ca/" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">Vérifier l'admissibilité au SARPA</a> pour une modification simplifiée.</li>}
              <li><a href="/orientation/aide-juridique" className="font-medium hover:underline">Vérifier votre admissibilité à l'aide juridique</a>.</li>
              <li>Utiliser le <a href="https://www.justice.gouv.qc.ca/table-de-fixation-pensions-alimentaires-enfants" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">simulateur de pension alimentaire du gouvernement</a> pour une estimation du montant.</li>
              <li><a href="/guides/pension" className="font-medium hover:underline">Consulter notre guide sur la pension alimentaire</a> pour plus de détails.</li>
              {answers.wantLawyer === 'true' && <li><a href="/orientation/cabinets" className="font-medium hover:underline">Contacter des avocats spécialisés en droit de la famille</a>.</li>}
            </ul>
            <p className="text-xs text-blue-600 pt-3 border-t border-blue-100 mt-4">Consultez un professionnel pour des conseils personnalisés à votre situation.</p>
          </div>
        </div>

        {/* FAQ Section Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">❓</span>
            Questions fréquentes (Pension)
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                <button
                  className="w-full text-left font-medium text-slate-700 flex items-center justify-between p-2 hover:bg-slate-50 rounded transition-colors"
                  onClick={() => setFaqOpenIndex(faqOpenIndex === index ? null : index)}
                  aria-expanded={faqOpenIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span>{item.question}</span>
                  <span className="text-blue-500 text-xl font-light">
                    {/* Corrected Icon rendering */}
                    {faqOpenIndex === index ? (
                      <MinusIcon className="w-4 h-4" />
                    ) : (
                      <PlusIcon className="w-4 h-4" />
                    )}
                  </span>
                </button>
                {/* Conditional rendering of the paragraph */}
                {faqOpenIndex === index && (
                  <p
                    id={`faq-answer-${index}`}
                    className="mt-2 text-slate-600 text-sm p-2 bg-slate-50 rounded"
                  >
                    {item.answer}
                  </p>
                )} {/* Closing brace for conditional render */}
              </div> // Closing div for map item
            ))} {/* Closing brace for map function */}
          </div> {/* Closing div for space-y-4 */}
        </div> {/* Closing div for FAQ card */} 
        {/* --- End of Corrected FAQ Section --- */}


        {/* Reset button */}
         <div className="pt-6 flex justify-center">
          <button
            onClick={handleReset}
             className={`btn ${
              showResetConfirm
                ? 'btn-error text-white' // Error style when confirmation active
                : 'btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400' // Outline style otherwise
            } btn-sm transition-colors gap-2`}
          >
            <ArrowPathIcon className="w-4 h-4" />
            {showResetConfirm ? 'Confirmer la réinitialisation ?' : 'Réinitialiser le calculateur'}
          </button>
        </div>

      </div> // Closing div for space-y-8
    );
  };


  // ────────────────────────────────────────────────────────────────────────────
  // 7) RENDU FINAL DU COMPOSANT (Adapted from GardeCalculator)
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50"> {/* Adjusted background */}
      {/* Removed DashNavbar as it's assumed to be part of a parent layout */}
      <div className="max-w-5xl mx-auto pb-12 px-4"> {/* Added horizontal padding */}

        {/* Updated Header Section */}
         <div className="px-4 md:px-6 py-8 mt-6 bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl border border-cyan-100 shadow-sm mb-8">
           <h1 className="text-2xl md:text-3xl font-bold text-cyan-700 mb-3 text-center">
             <span className="inline-block h-1 bg-teal-400 w-8 mr-2 rounded-full align-middle"></span>
-            Calculateur des Coûts (Pension Alimentaire)
+            Calculateur des Coûts (Modification de Pension Alimentaire)
             <span className="inline-block h-1 bg-teal-400 w-8 ml-2 rounded-full align-middle"></span>
           </h1>
           <p className="text-center text-cyan-600 max-w-3xl mx-auto">
-            Estimez les coûts potentiels associés à une procédure de demande ou de modification de pension alimentaire au Québec.
+            Estimez les coûts potentiels de votre procédure de modification de pension alimentaire selon votre situation personnelle.
           </p>
         </div>

        {/* Top cards: cost summary and progression */}
         <div className="grid md:grid-cols-2 gap-6 mb-8">
           {/* Card 1: Cost Summary */}
           <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
             <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
               <span className="bg-blue-50 p-2 rounded-full mr-2">💰</span>
               Estimation des coûts
             </h3>
             <div className="flex items-center justify-center h-16">
               {totalCost.min === 0 && totalCost.max === 0 && Object.keys(answers).length > 0 ? (
                  // Case where answers exist but result in 0 cost (e.g., no lawyer, no fees)
                  <p className="text-green-600 text-center text-lg font-bold">
                     0$ (selon vos choix)
                  </p>
               ) : totalCost.min === 0 && totalCost.max === 0 ? (
                 <p className="text-slate-500 text-center text-sm">
                   Complétez le questionnaire pour voir l'estimation
                 </p>
               ) : (
                 <div className="text-center">
                   <p className="text-2xl font-bold text-blue-700">
                     {totalCost.min.toLocaleString()}$ - {totalCost.max.toLocaleString()}$
                   </p>
                   <p className="text-sm text-slate-600 mt-1">coût total estimé</p>
                 </div>
               )}
             </div>
           </div>

           {/* Card 2: Progression */}
           <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
             <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
               <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
               Progression du questionnaire
             </h3>
             <div className="mb-1"> {/* Reduced bottom margin */}
               <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold">{progressPercent.toFixed(0)}% complété</span>
                 <span className="text-slate-500 text-xs">
                   {sections.filter(s => completedSections[s.id]).length} / {sections.length} sections
                 </span>
               </div>
               <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden"> {/* Ensure overflow hidden for smooth edges */}
                 <div
                   className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out" // Added gradient
                   style={{ width: `${progressPercent}%` }}
                 ></div>
               </div>
             </div>
           </div>
         </div>


         {/* Legal Aid & SARPA Info Box */}
         {renderLegalAidInfo()}

         {/* Main questionnaire section using renderSection */}
         <div className="relative mb-8"> {/* Removed padding, handled by container */}
           {/* Vertical line removed for cleaner look, sections provide visual separation */}
           {/* <div className="absolute bg-slate-200 w-[3px] h-full left-4 md:left-6 top-0 z-0" /> */}
           <div className="space-y-4"> {/* Reduced space between sections */}
             {sections.map(renderSection)}
           </div>
         </div>

          {/* Final Legal Note */}
         <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 text-xs text-slate-500 mt-8">
            <p className="italic text-center">
              <strong>Avis important :</strong> Ce calculateur fournit une estimation générale à titre informatif seulement.
              Les coûts réels peuvent varier significativement selon les spécificités de votre dossier, les tarifs des professionnels choisis, et la durée de la procédure.
              Cette estimation ne constitue pas un avis juridique. Consultez un avocat pour des conseils adaptés à votre situation.
            </p>
         </div>

       </div>
    </div>
   );
}

// Wrap with requireAuth if authentication is needed
export const Component = requireAuth(PensionCalculator);

export default PensionCalculator; // Keep default export if used directly in routing
